import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, queuedMessage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Save, AlertTriangle, Loader2, Disc3, UserRound, Clock } from "lucide-react";
import { availInfo, isRepolishPhase, nextProcessFor, phaseLabel, phaseTone, todayStr } from "./dprUtils";

const fmt = (n) => (Number(n) || 0).toLocaleString();

function BigNumber({ id, label, value, onChange, tone = "slate", hint }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>
      <input data-testid={id} type="number" min="0" inputMode="numeric" value={value}
        onChange={(e) => onChange(e.target.value)} onFocus={(e) => e.target.select()}
        className={`w-full border-2 rounded-xl px-4 py-3 text-2xl font-mono font-semibold text-right outline-none transition-colors ${tone === "red" ? "border-red-300 bg-red-50 text-red-700 focus:border-red-500" : "border-slate-300 focus:border-blue-500 text-slate-800"}`} />
      {hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

export function StepQuantities({ process, model, processes, links, meta, setMeta, isOp, canBackdate, initial, onSaved, onChangeSelection, currentUser }) {
  const editing = !!initial?.sid;
  const [input, setInput] = useState(initial?.input_qty ?? "");
  const [rej, setRej] = useState(initial?.rejected_qty ?? 0);
  const [coilId, setCoilId] = useState(initial?.coil_id || "");
  const [wastage, setWastage] = useState(initial?.coil_wastage_kg ?? 0);
  const [repolish, setRepolish] = useState(initial?.repolish_qty ?? 0);
  const [remarks, setRemarks] = useState(initial?.remarks || "");
  const [coilQuery, setCoilQuery] = useState("");
  const [operatorId, setOperatorId] = useState(initial?.operator_id || currentUser?.id || "");
  const [startTime, setStartTime] = useState(initial?.start_time || "");
  const [endTime, setEndTime] = useState(initial?.end_time || "");
  const [users, setUsers] = useState([]);
  const [backdateConfirm, setBackdateConfirm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ av: null, consumed: null, coils: [], per: 0 });

  useEffect(() => { api.get("/users/names").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const [avRes, invRes, bomRes] = await Promise.all([
          api.get(`/process-availability/${process.id}`, { params: { model } }),
          api.get("/inventory-items"),
          api.get("/model-bom", { params: { model_name: model } }),
        ]);
        const consumed = invRes.data.find((x) => x.id === process.consumes_item_id) || null;
        let coils = [];
        if (consumed?.is_coil) {
          const r = await api.get(`/inventory-items/${consumed.id}/coils`);
          coils = r.data.filter((c) => c.status !== "consumed" && (c.remaining_weight_kg ?? 0) > 0).sort((a, b) => String(a.coil_no).localeCompare(String(b.coil_no), undefined, { numeric: true }));
        }
        const bomPer = bomRes.data.find((r) => r.item_id === process.consumes_item_id)?.qty_per_bottle;
        const per = avRes.data?.coil_per || bomPer || 0;
        if (alive) setData({ av: avRes.data, consumed, coils, per });
      } catch (e) { toast.error(formatApiError(e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [process.id, process.consumes_item_id, model]);

  const isCoil = !!data.consumed?.is_coil;
  const coil = isCoil ? data.coils.find((c) => c.id === coilId) : null;
  const avail = useMemo(() => availInfo({ consumedItem: data.consumed, per: data.per, coil, coilRequired: isCoil, av: data.av }), [data, coil, isCoil]);
  const mult = process.multiplier || 1;
  const inQ = Number(input || 0), rejQ = Number(rej || 0);
  const good = Math.max(0, (inQ - rejQ) * mult);
  const over = inQ > avail.max + 1e-6;
  const rejOver = rejQ > inQ;
  const next = nextProcessFor(model, process.id, links, processes);
  const isBackdated = meta.date < todayStr();
  const backdateOk = backdateConfirm.trim().toUpperCase() === "CONFIRM";
  const blocked = loading || saving || over || rejOver || inQ <= 0 || (isCoil && !coilId) || (canBackdate && isBackdated && !backdateOk);
  const comps = (data.av?.components || []).filter((c) => !isOp || c.is_bottleneck || inQ > c.units + 1e-6);
  const tone = phaseTone(process.phase);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post("/dpr-mes/row", {
        date: meta.date, shift: meta.shift, model, process_id: process.id,
        input_qty: inQ, rejected_qty: rejQ, coil_id: coilId || null, coil_wastage_kg: Number(wastage || 0),
        remarks: remarks || "", repolish_qty: Number(repolish || 0), sid: initial?.sid || null,
        allow_backdate: canBackdate && isBackdated && backdateOk,
        operator_id: operatorId || null, operator_name: users.find((u) => u.id === operatorId)?.name || (operatorId === currentUser?.id ? currentUser?.name : "") || "",
        start_time: startTime || null, end_time: endTime || null,
      });
      const q = queuedMessage(res);
      onSaved({ good, next, queued: q, coilNo: coil?.coil_no });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="wiz-step-qty">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`stat-pill ${tone.chip}`}>{phaseLabel(process.phase)}</span>
        <span className="font-heading text-lg font-semibold text-slate-900">{process.name}</span>
        <ArrowRight size={16} className="text-slate-300" />
        <span className="text-sm font-medium text-slate-600" data-testid="wiz-selected-model">{model}</span>
        {!editing && <button data-testid="wiz-change-selection" onClick={onChangeSelection} className="ml-auto text-xs font-semibold text-blue-600 hover:text-blue-700">Change</button>}
        {editing && <span className="ml-auto stat-pill bg-emerald-100 text-emerald-700">Editing saved entry</span>}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-sm"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Date</span>
          <input data-testid="wiz-date" type="date" max={todayStr()} value={meta.date} disabled={editing} onChange={(e) => { setBackdateConfirm(""); setMeta({ ...meta, date: e.target.value }); }} className="border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-50" /></label>
        <label className="text-sm"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Shift</span>
          <select data-testid="wiz-shift" value={meta.shift} onChange={(e) => setMeta({ ...meta, shift: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2 text-sm"><option>Day</option><option>Night</option><option>General</option></select></label>
        {isBackdated && !canBackdate && <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">Back-dated: only allowed after your last recorded day.</span>}
      </div>
      {canBackdate && isBackdated && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm" data-testid="wiz-backdate-warning">
          <b className="text-amber-800">Back-dated entry.</b> <span className="text-amber-700">Type CONFIRM to enable saving.</span>
          <input data-testid="wiz-backdate-confirm" value={backdateConfirm} onChange={(e) => setBackdateConfirm(e.target.value)} placeholder="CONFIRM" className={`ml-2 border rounded px-2 py-1 text-sm w-32 ${backdateOk ? "border-emerald-400 bg-emerald-50" : "border-amber-300"}`} />
        </div>
      )}

      <div className={`rounded-xl border p-4 ${over ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`} data-testid="wiz-available-box">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Available to process</div>
            {loading ? <div className="flex items-center gap-2 text-slate-400 mt-1"><Loader2 size={16} className="animate-spin" /> checking stock…</div>
              : <div className={`font-heading text-3xl font-bold mt-0.5 ${avail.needCoil ? "text-amber-600" : over ? "text-red-600" : "text-slate-900"}`} data-testid="wiz-available-text">{avail.text}</div>}
            {!loading && <div className="text-xs text-slate-500 mt-0.5">{avail.sub}</div>}
          </div>
          {!loading && !isCoil && avail.max > 0 && !editing && (
            <button data-testid="wiz-use-all" onClick={() => setInput(avail.max)} className="text-xs font-semibold text-blue-600 bg-white border border-blue-200 hover:bg-blue-50 rounded-lg px-3 py-1.5">Use all</button>
          )}
        </div>
        {comps.length > 0 && (
          <div className="mt-3 space-y-1 border-t border-slate-200 pt-2">
            {comps.map((c) => {
              const short = inQ > c.units + 1e-6;
              return (
                <div key={c.item_id} data-testid={`wiz-comp-${c.item_id}`} className={`text-xs flex items-center gap-1.5 ${short ? "text-red-600 font-semibold" : c.is_bottleneck ? "text-amber-600" : "text-slate-500"}`}>
                  {(short || c.is_bottleneck) && <AlertTriangle size={12} />}
                  <span>{c.name}: {fmt(c.stock)} {c.unit} → enough for {fmt(c.units)} pcs</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isCoil && !loading && (
        <div data-testid="wiz-coil-picker">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Which coil is loaded? <span className="text-slate-400 normal-case">({data.coils.length} in stock)</span></div>
          {data.coils.length === 0 && !coilId && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">No coil with stock. Receive & weigh a coil in Inventory first.</div>}
          {data.coils.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-2">
              {data.coils.length > 8 && <input data-testid="wiz-coil-search" value={coilQuery} onChange={(e) => setCoilQuery(e.target.value)} placeholder="Type to filter coil no…" className="sm:w-48 border border-slate-300 rounded-lg px-3 py-2 text-sm" />}
              <select data-testid="wiz-coil-select" value={coilId} onChange={(e) => setCoilId(e.target.value)} size={1}
                className={`flex-1 border-2 rounded-lg px-3 py-2 text-sm font-medium ${coilId ? "border-amber-500 bg-amber-50" : "border-slate-300"}`}>
                <option value="">Select coil…</option>
                {data.coils.filter((c) => !coilQuery || String(c.coil_no).toLowerCase().includes(coilQuery.toLowerCase())).map((c) => (
                  <option key={c.id} value={c.id}>{`${c.coil_no} — ${fmt(c.remaining_weight_kg)} kg left`}</option>
                ))}
                {coilId && !data.coils.find((c) => c.id === coilId) && <option value={coilId}>(previously selected coil — now consumed)</option>}
              </select>
            </div>
          )}
          {coil && <div className="mt-1.5 text-xs text-slate-500 flex items-center gap-1" data-testid="wiz-coil-selected"><Disc3 size={12} className="text-amber-600" /> {coil.coil_no}{coil.supplier_code ? ` · ${coil.supplier_code} · ${coil.thickness_mm} mm · ${coil.grade}` : ""} · {fmt(coil.remaining_weight_kg)} kg left</div>}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <BigNumber id="wiz-input" label="Pieces IN" value={input} onChange={setInput} tone={over ? "red" : "slate"} hint={over ? `Only ${fmt(avail.max)} available` : undefined} />
        <BigNumber id="wiz-rejected" label="Rejected" value={rej} onChange={setRej} tone={rejOver ? "red" : "slate"} hint={rejOver ? "Cannot exceed pieces in" : undefined} />
      </div>

      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center justify-between gap-3" data-testid="wiz-good-box">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Good output</div>
          <div className="text-xs text-emerald-700/80 mt-0.5">{mult !== 1 ? `(${fmt(inQ)} − ${fmt(rejQ)}) × ${mult}` : `${fmt(inQ)} − ${fmt(rejQ)}`}{next ? <> · goes to <b>{next.name}</b></> : " · final stage"}</div>
        </div>
        <div className="font-heading text-3xl font-bold text-emerald-700" data-testid="wiz-good-qty">{fmt(good)}</div>
      </div>

      <div className="rounded-xl border border-slate-200 p-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="wiz-operator-box">
        <label className="block sm:col-span-1"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><UserRound size={12} /> Operator on machine</span>
          <select data-testid="wiz-operator" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">— not recorded —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            {operatorId && !users.find((u) => u.id === operatorId) && <option value={operatorId}>{initial?.operator_name || currentUser?.name || "selected"}</option>}
          </select></label>
        <label className="block"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Clock size={12} /> From</span>
          <input data-testid="wiz-start-time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></label>
        <label className="block"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Clock size={12} /> To</span>
          <input data-testid="wiz-end-time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></label>
        <p className="sm:col-span-3 text-[11px] text-slate-400 -mt-1">Operator changed mid-shift? Save this entry, then use “Another entry” for the next operator with their time and quantity.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {isCoil && <label className="block"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Coil wastage (kg)</span><input data-testid="wiz-wastage" type="number" min="0" step="0.001" value={wastage} onChange={(e) => setWastage(e.target.value)} className="w-full border border-amber-300 bg-amber-50 rounded-lg px-3 py-2 text-sm text-right" /></label>}
        {isRepolishPhase(process.phase) && <label className="block"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Repolish (pcs)</span><input data-testid="wiz-repolish" type="number" min="0" value={repolish} onChange={(e) => setRepolish(e.target.value)} className="w-full border border-orange-300 bg-orange-50 rounded-lg px-3 py-2 text-sm text-right" /></label>}
        <label className="block sm:col-span-1"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Remarks <span className="text-slate-400 normal-case">(optional)</span></span><input data-testid="wiz-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="reason / note" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></label>
      </div>

      <button data-testid="wiz-save" onClick={save} disabled={blocked}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl py-3.5 text-base font-semibold flex items-center justify-center gap-2 transition-colors">
        {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} {editing ? "Update entry" : "Save entry"}
      </button>
      {isOp && editing && <p className="text-xs text-center text-slate-500">Edits by operators are sent to admin for approval.</p>}
    </div>
  );
}
